import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { ShieldAlert, LogOut, KeyRound, CreditCard, Monitor, Smartphone, Bell, Star, Volume2, VolumeX, UserCog } from "lucide-react";
import { useLiveRatings } from "./useLiveRatings";
import { Avatar } from "./common";
import { ChangePasswordDialog } from "./ChangePassword";
import ProfileEditDialog from "./ProfileEditDialog";
import api from "../lib/api";
import { setTimeZones, fmtTime } from "../lib/time";

const warn = (what) => (e) => console.warn(`${what} failed`, e);

export function useTimeZonesReady(orgId) {
  const [ready, setReady] = useState(!orgId);
  useEffect(() => {
    if (!orgId) { setReady(true); return; }
    Promise.all([api.get("/organization"), api.get("/locations")])
      .then(([o, l]) => setTimeZones(o.data.organization?.timezone, l.data))
      .catch(warn("Time zone load"))
      .finally(() => setReady(true));
  }, [orgId]);
  return ready;
}

export function useViewportMode() {
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("sv_view_mode") || "desktop");
  const setView = (mode) => { setViewMode(mode); localStorage.setItem("sv_view_mode", mode); };
  // Phones render the full desktop layout (default) or the responsive mobile layout.
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) { return undefined; }
    const prev = meta.getAttribute("content");
    meta.setAttribute("content", viewMode === "mobile" ? "width=device-width, initial-scale=1" : "width=1280");
    return () => { meta.setAttribute("content", prev || "width=device-width, initial-scale=1"); };
  }, [viewMode]);
  return [viewMode, setView];
}

export function usePinnedSidebar(setOpen) {
  const [pinned, setPinned] = useState(() => localStorage.getItem("sv_sidebar_pinned") === "1");
  const togglePin = () => setPinned((p) => {
    const next = !p;
    localStorage.setItem("sv_sidebar_pinned", next ? "1" : "0");
    if (next) setOpen(false);
    return next;
  });
  return [pinned, togglePin];
}

export function useLayoutData(role) {
  const [notes, setNotes] = useState([]);
  const [org, setOrg] = useState(null);
  const [locName, setLocName] = useState(null);
  const [billing, setBilling] = useState(null);
  useEffect(() => {
    api.get("/notifications").then((r) => setNotes(r.data)).catch(warn("Notifications load"));
    if (["corporate_admin", "store_admin", "employee"].includes(role)) api.get("/organization").then((r) => setOrg(r.data.organization)).catch(warn("Organization load"));
    if (["corporate_admin", "store_admin"].includes(role)) api.get("/billing").then((r) => setBilling(r.data)).catch(warn("Billing load"));
    if (role === "store_admin") api.get("/locations").then((r) => setLocName(r.data?.[0]?.name || null)).catch(warn("Locations load"));
  }, [role]);
  return { notes, org, locName, billing };
}

const toLiveNote = (x) => ({
  id: `live-${x.id}`, live: true, low: x.rating <= 2, read: false,
  title: `New ${x.rating}-star rating`,
  body: `${x.employee_name || "Store rating"}${x.location_name ? ` · ${x.location_name}` : ""} · ${fmtTime(x.created_at, x.location_id)}`,
});

export function useLiveNotes(isAdmin, notes) {
  const [liveNotes, setLiveNotes] = useState([]);
  const { muted, toggleMute } = useLiveRatings(isAdmin, (items) => setLiveNotes((prev) => [...items.slice().reverse().map(toLiveNote), ...prev].slice(0, 50)));
  const markLiveRead = () => setLiveNotes((prev) => prev.map((n) => ({ ...n, read: true })));
  return { allNotes: [...liveNotes, ...notes], muted, toggleMute, markLiveRead };
}

export function ImpersonationBanner({ name, impersonator, refresh }) {
  const navigate = useNavigate();
  const stop = async () => {
    try { await api.post("/platform/stop-impersonation"); await refresh(); navigate("/app/platform"); } catch (e) { console.warn("Stop impersonation failed", e); }
  };
  return (
    <div className="flex items-center justify-between gap-3 bg-amber-500 px-4 py-2 text-sm font-semibold text-white md:px-8" data-testid="impersonation-banner">
      <span className="flex items-center gap-2"><ShieldAlert className="h-4 w-4" />Viewing as <b>{name}</b> — signed in by {impersonator.email}</span>
      <button onClick={stop} data-testid="exit-impersonation" className="rounded-md bg-white/20 px-3 py-1 text-xs font-bold uppercase tracking-wide transition-colors hover:bg-white/30">Exit to platform</button>
    </div>
  );
}

export function PlanBadge({ billing, isCorp, onNavigate }) {
  const s = billing.subscription;
  return (
    <NavLink to="/app/billing" onClick={onNavigate} data-testid="plan-badge"
      className="mt-auto mb-1 flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm transition-colors hover:border-indigo-200">
      <span className="flex items-center gap-2 font-semibold text-slate-700">
        <CreditCard className="h-[18px] w-[18px] text-indigo-600" />{s.plan_name} plan
        {(s.overdue || s.awaiting_payment || billing.org_status === "suspended") && <span className="h-2 w-2 rounded-full bg-red-500" data-testid="plan-badge-alert" />}
      </span>
      {isCorp && (s.price === 0 || s.max_employees != null) && <span className="rounded-full bg-indigo-600 px-2 py-0.5 text-[11px] font-bold text-white" data-testid="plan-upgrade-cta">Upgrade</span>}
    </NavLink>
  );
}

export function SidebarAccount({ phoneAuth, onLogout }) {
  return (
    <>
      {phoneAuth ? <div className="mt-auto" /> : <ChangePasswordDialog trigger={
        <button data-testid="change-password-nav" className="mt-auto flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900">
          <KeyRound className="h-[18px] w-[18px]" /> Change password
        </button>} />}
      <button onClick={onLogout} data-testid="logout-button" className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-red-50 hover:text-red-600">
        <LogOut className="h-[18px] w-[18px]" /> Sign out
      </button>
    </>
  );
}

export function ViewToggle({ viewMode, setView }) {
  const btn = (mode, Icon, label) => (
    <button onClick={() => setView(mode)} data-testid={`view-${mode}`} aria-label={label} title={label}
      className={`rounded-md p-1.5 transition-colors ${viewMode === mode ? "bg-indigo-50 text-indigo-600" : "text-slate-400 hover:text-slate-600"}`}>
      <Icon className="h-4 w-4" />
    </button>
  );
  return <div className="flex items-center rounded-lg border border-slate-200 p-0.5" data-testid="view-toggle">{btn("desktop", Monitor, "Computer view")}{btn("mobile", Smartphone, "Phone view")}</div>;
}

export function SoundToggle({ muted, toggleMute }) {
  return (
    <button onClick={toggleMute} data-testid="rating-sound-toggle" aria-label={muted ? "Turn rating sound on" : "Turn rating sound off"}
      title={muted ? "Rating sound off" : "Rating sound on"} className={`rounded-lg p-2 transition-colors hover:bg-slate-100 ${muted ? "text-slate-400" : "text-indigo-600"}`}>
      {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
    </button>
  );
}

function NoteRow({ n }) {
  return (
    <div className="rounded-lg px-2 py-2 text-sm hover:bg-slate-50" data-testid={n.live ? "live-rating-note" : "notification-item"}>
      <p className={`flex items-center gap-1 font-semibold ${n.low ? "text-red-600" : "text-slate-800"}`}>
        {n.live && <Star className={`h-3.5 w-3.5 ${n.low ? "fill-red-500 text-red-500" : "fill-amber-400 text-amber-400"}`} />}{n.title}
      </p>
      <p className="text-slate-500">{n.body}</p>
    </div>
  );
}

export function NotificationsMenu({ allNotes, isAdmin, onOpen }) {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const unread = allNotes.filter((n) => !n.read).length;
  const toggle = () => setShow((s) => { if (!s) onOpen(); return !s; });
  return (
    <div className="relative">
      <button onClick={toggle} data-testid="notifications-button" className="relative rounded-lg p-2 text-slate-500 hover:bg-slate-100">
        <Bell className="h-5 w-5" />
        {unread > 0 && <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-red-500" />}
      </button>
      {show && (
        <div className="absolute right-0 mt-2 w-80 rounded-xl border border-slate-200 bg-white p-2 shadow-lg" data-testid="notifications-panel">
          <div className="flex items-center justify-between px-2 py-1">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Notifications</p>
            {isAdmin && <button onClick={() => { setShow(false); navigate("/app/my-notifications"); }} data-testid="notification-settings-link" className="text-xs font-semibold text-indigo-600 hover:underline">Settings</button>}
          </div>
          {allNotes.length === 0 && <p className="px-2 py-4 text-center text-sm text-slate-400">All caught up</p>}
          {allNotes.slice(0, 8).map((n) => <NoteRow key={n.id} n={n} />)}
        </div>
      )}
    </div>
  );
}

export function UserChip({ user }) {
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="user-chip-button"
        className="flex items-center gap-2 rounded-lg px-1 py-1 transition-colors hover:bg-slate-100"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Avatar name={user?.name} src={user?.picture} size={34} />
        <div className="hidden sm:block text-left">
          <p className="text-sm font-semibold leading-tight text-slate-800">{user?.name}</p>
          <p className="text-[11px] uppercase tracking-wide text-slate-400">{(user?.role || "").replace("_", " ")}</p>
        </div>
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 rounded-xl border border-slate-200 bg-white p-1 shadow-lg" role="menu" data-testid="user-chip-menu">
          <div className="px-3 py-2">
            <p className="truncate text-sm font-semibold text-slate-800">{user?.name}</p>
            <p className="truncate text-xs text-slate-400">{user?.email}</p>
          </div>
          <div className="my-1 border-t border-slate-100" />
          <button
            type="button"
            onClick={() => { setOpen(false); setEditOpen(true); }}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            data-testid="user-chip-edit-profile"
          >
            <UserCog className="h-4 w-4 text-slate-500" /> Edit profile
          </button>
        </div>
      )}
      <ProfileEditDialog open={editOpen} onOpenChange={setEditOpen} />
    </div>
  );
}

export function HeaderOrg({ org, locName }) {
  return (
    <div className="hidden items-center gap-2.5 md:flex" data-testid="header-org">
      {org?.logo_url ? <img src={org.logo_url} alt={org.name} className="h-8 w-8 rounded-md object-contain" data-testid="header-org-logo" /> : null}
      <span className="text-sm font-semibold text-slate-700">{org?.name || "Service Intelligence Platform"}</span>
      {locName && <span className="text-sm text-slate-400" data-testid="header-location">· {locName}</span>}
    </div>
  );
}
